import React, { useEffect, useState } from "react";
import Nav from "./Navigation";
import { getTokenInternal } from "./useToken";

export default function AllListedItems() {
  const [allItems, setAllItems] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [token, setToken] = useState(null);
  const [selectedItemName, setSelectedItemName] = useState(null);

  // --- Translation support (same pattern as myItems.js) ---
  // Defaults to English (language_id 1) when no one is logged in.
  const [typeTranslations, setTypeTranslations] = useState({});
  const [eventTranslations, setEventTranslations] = useState({});
  const [mapTranslations, setMapTranslations] = useState({});
  const [mapsNameTranslations, setMapsNameTranslations] = useState({});
  const [itemNameTranslations, setItemNameTranslations] = useState({});

  const translateType = (rawType) => typeTranslations[rawType] || rawType;
  const translateEvent = (rawEvent) => eventTranslations[rawEvent] || rawEvent;
  const translateMap = (rawMap) => mapTranslations[rawMap] || mapsNameTranslations[rawMap] || rawMap;
  const translateItemName = (rawName) => itemNameTranslations[rawName] || rawName;

  // --- Unlock requirements (preorder/DLC gating) ---
  const [requirementsByItem, setRequirementsByItem] = useState({});
  const [requirementNames, setRequirementNames] = useState({});
  const [myRequirementIds, setMyRequirementIds] = useState([]);

  const lockedRequirementNames = (itemId) => {
    const requirementIds = requirementsByItem[itemId];
    if (!requirementIds || requirementIds.length === 0) return null;
    if (requirementIds.some((id) => myRequirementIds.includes(id))) return null;
    return requirementIds.map((id) => requirementNames[id] || `#${id}`).join(" or ");
  };

  const fetchData = async () => {
    try {
      const url = `${process.env.REACT_APP_API_HOST}/api/items`;
      const response = await fetch(url);
      const completeList = await response.json();
      setAllItems(completeList);
    } catch (error) {
      console.error("Failed to fetch items:", error);
    }
  };

  useEffect(() => {
    const fetchUnlockRequirements = async () => {
      try {
        const gamesRes = await fetch(`${process.env.REACT_APP_API_HOST}/api/games`);
        const games = gamesRes.ok ? await gamesRes.json() : [];

        const byItemResults = await Promise.all(
          games.map((game) =>
            fetch(`${process.env.REACT_APP_API_HOST}/api/unlock-requirements/by-item?games_id=${game.id}`)
              .then((res) => (res.ok ? res.json() : {}))
          )
        );
        const mergedByItem = {};
        byItemResults.forEach((byItem) => {
          Object.entries(byItem).forEach(([itemId, requirementIds]) => {
            mergedByItem[itemId] = requirementIds;
          });
        });
        setRequirementsByItem(mergedByItem);

        const allRequirementsRes = await fetch(`${process.env.REACT_APP_API_HOST}/api/unlock-requirements`);
        if (allRequirementsRes.ok) {
          const allRequirements = await allRequirementsRes.json();
          const nameMap = {};
          allRequirements.forEach((req) => { nameMap[req.id] = req.name; });
          setRequirementNames(nameMap);
        }

        const token = await getTokenInternal();
        if (token) {
          const myRes = await fetch(`${process.env.REACT_APP_API_HOST}/api/unlock-requirements/my-requirements`, {
            headers: { Authorization: `Bearer ${token.access_token}` },
          });
          if (myRes.ok) setMyRequirementIds(await myRes.json());
        }
      } catch (error) {
        console.error("Error fetching unlock requirements:", error.message);
      }
    };

    fetchUnlockRequirements();
  }, []);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const fetchedToken = await getTokenInternal();
        setToken(fetchedToken);
      } catch (error) {
        console.error("Error fetching token:", error);
      }
    };

    fetchToken();
    fetchData();
  }, []);

  useEffect(() => {
    const fetchTranslations = async () => {
      try {
        // Don't trust token.account.language_id -- it's baked into the JWT
        // at login time, so it goes stale the moment someone changes their
        // language in My Profile without logging back in. Look up the
        // live value instead.
        let languageId = 1;
        if (token?.account?.username) {
          const accountRes = await fetch(
            `${process.env.REACT_APP_API_HOST}/api/accounts/${encodeURIComponent(token.account.username)}`
          );
          if (accountRes.ok) {
            const accountData = await accountRes.json();
            languageId = accountData.language_id || 1;
          }
        }

        const [typesRes, eventRes, mapsRes, mapsNameRes, itemsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_HOST}/api/translations/lookup/types?language_id=${languageId}`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/translations/lookup/event?language_id=${languageId}`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/translations/lookup/maps?language_id=${languageId}`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/translations/lookup/maps_name?language_id=${languageId}`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/translations/lookup/items?language_id=${languageId}`),
        ]);

        if (typesRes.ok) setTypeTranslations(await typesRes.json());
        if (eventRes.ok) setEventTranslations(await eventRes.json());
        if (mapsRes.ok) setMapTranslations(await mapsRes.json());
        if (mapsNameRes.ok) setMapsNameTranslations(await mapsNameRes.json());
        if (itemsRes.ok) setItemNameTranslations(await itemsRes.json());
      } catch (error) {
        console.error("Error fetching translations:", error.message);
      }
    };

    fetchTranslations();
  }, [token]);

  const filterItems = (items) => {
    let filtered = items;
    if (selectedEvent) {
      filtered = filtered.filter((item) => item.event === selectedEvent);
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        item.name.toLowerCase().includes(q) ||
        translateItemName(item.name).toLowerCase().includes(q)
      );
    }
    setSelectedEvents(filtered);
  };

  useEffect(() => {
    filterItems(allItems);
  }, [selectedEvent, searchTerm, allItems, itemNameTranslations]);

  const handleEventSelection = (e) => {
    setSelectedEvent(e.target.value);
  };

  const handleClearSelection = () => {
    setSelectedEvent("");
    setSearchTerm("");
  };

  const toggleVisibility = (name) => {
    setSelectedItemName((prev) => (prev === name ? null : name));
  };

  return (
    <div>
      <Nav />
      <div className="container mx-auto text-white p-4">
        <div className="Title text-2xl font-bold mb-4">Complete List of Items</div>

        <div className="mb-4">
          <label htmlFor="eventDropdown" className="text-md font-bold">
            Select Event:
          </label>
          <select
            id="eventDropdown"
            onChange={handleEventSelection}
            value={selectedEvent}
            className="ml-2 p-1 border rounded text-black"
          >
            <option value="">All</option>
            {Array.from(new Set(allItems.map((item) => item.event)))
              .sort((a, b) => a.localeCompare(b))
              .map((event) => (
                <option key={event} value={event}>
                  {translateEvent(event)}
                </option>
              ))}
          </select>

          <span className="ml-4 mr-2">Or search</span>
          <input
            type="text"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ml-2 p-1 border rounded text-black"
          />
          <button
            onClick={handleClearSelection}
            className="ml-2 p-1 border rounded text-black bg-gray-200 hover:bg-gray-300"
          >
            Reset/Clear
          </button>
        </div>

        <div className="flex gap-4">
          <div className="flex-1 flex flex-wrap">
            {Array.from(
              new Set(
                selectedEvents
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((item) => item.maps)
              )
            ).map((map) => (
              <div key={map} className="mb-6 ml-2 w-full md:w-auto">
                <h3 className="text-lg font-bold mb-2">{translateMap(map)}</h3>
                {Array.from(
                  new Set(
                    selectedEvents
                      .filter((item) => item.maps === map)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((item) => item.types)
                  )
                ).map((type) => (
                  <div key={type} className="mb-4">
                    <h4 className="text-md font-bold mb-2">{translateType(type)}</h4>
                    <div className="grid gap-2">
                      {selectedEvents
                        .filter((item) => item.maps === map && item.types === type)
                        .map((filteredItem) => (
                          <div
                            className={`border p-1 rounded ${
                              filteredItem.checked ? "bg-green-500" : "bg-neutral-400"
                            } flex items-center ${
                              selectedItemName === filteredItem.name ? "border-yellow-400 border-4" : ""
                            }`}
                            key={filteredItem.user_item_id}
                          >
                            <div>{translateItemName(filteredItem.name)}</div>
                            {lockedRequirementNames(filteredItem.id) && (
                              <span
                                className="ml-2 text-xs text-yellow-300"
                                title={`Requires: ${lockedRequirementNames(filteredItem.id)}`}
                              >
                                🔒 {lockedRequirementNames(filteredItem.id)}
                              </span>
                            )}
                            <button
                              className="ml-2 bg-blue-500 border-solid border-2 border-white text-white px-1 py-0 rounded"
                              onClick={() => toggleVisibility(filteredItem.name)}
                            >
                              {selectedItemName === filteredItem.name ? "Hide" : "Info"}
                            </button>

                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Sticky Image Panel */}
          {selectedItemName && (
            <div className="w-1/4 sticky top-4 self-start border border-yellow-400 p-4 rounded bg-neutral-900">
              <h2 className="text-xl font-bold mb-2 text-white">Selected Item</h2>
              {selectedEvents.map((item) =>
                item.name === selectedItemName ? (
                  <div key={item.user_item_id}>
                    <img
                      className="object-scale-down h-64 w-full rounded"
                      src={`${process.env.REACT_APP_R2_PUBLIC_URL}/${item.url}`}
                      alt="Selected render"
                      onError={(e) => {
                        e.target.src = "/gamelogos/Unofficial.PNG";
                      }}
                    />
                    <div className="mt-2 text-white font-semibold">{translateItemName(item.name)}</div>
                    {lockedRequirementNames(item.id) && (
                      <div className="mt-1 text-sm text-yellow-300">
                        🔒 Requires: {lockedRequirementNames(item.id)}
                      </div>
                    )}
                  </div>
                ) : null
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
